import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { companyInfo } from '../data/mock';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Cookie Policy
          </h1>
          <p className="text-slate-400 mb-8">
            Last Updated: January 27, 2026
          </p>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Mind Flex Games, LLC ("Company," "we," "us," or "our") uses cookies and similar tracking technologies on our website. This Cookie Policy explains what cookies are, how we use them, and your choices regarding their use.
              </p>
              <p className="leading-relaxed mt-4">
                By continuing to use our website, you consent to our use of cookies in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. What Are Cookies?</h2>
              <p className="leading-relaxed">
                Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently, provide information to website owners, and enhance user experience.
              </p>
              <p className="leading-relaxed mt-4">
                Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device for a set period or until you delete them, while session cookies are deleted when you close your web browser.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-lg font-medium text-white mb-2 mt-6">Essential Cookies</h3>
              <p className="leading-relaxed">
                These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.
              </p>

              <h3 className="text-lg font-medium text-white mb-2 mt-6">Analytics Cookies</h3>
              <p className="leading-relaxed">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's functionality and content.
              </p>

              <h3 className="text-lg font-medium text-white mb-2 mt-6">Functional Cookies</h3>
              <p className="leading-relaxed">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings. They may be set by us or by third-party providers whose services we have added to our pages.
              </p>

              <h3 className="text-lg font-medium text-white mb-2 mt-6">Marketing Cookies</h3>
              <p className="leading-relaxed">
                These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant advertisements on other sites. They do not directly store personal information but are based on uniquely identifying your browser and internet device.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="w-full mt-4 border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-white">Cookie Name</th>
                      <th className="text-left py-3 px-4 text-white">Type</th>
                      <th className="text-left py-3 px-4 text-white">Purpose</th>
                      <th className="text-left py-3 px-4 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">_session</td>
                      <td className="py-3 px-4">Essential</td>
                      <td className="py-3 px-4">Session management</td>
                      <td className="py-3 px-4">Session</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">_ga</td>
                      <td className="py-3 px-4">Analytics</td>
                      <td className="py-3 px-4">Google Analytics tracking</td>
                      <td className="py-3 px-4">2 years</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">_gid</td>
                      <td className="py-3 px-4">Analytics</td>
                      <td className="py-3 px-4">Google Analytics user distinction</td>
                      <td className="py-3 px-4">24 hours</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">cookie_consent</td>
                      <td className="py-3 px-4">Essential</td>
                      <td className="py-3 px-4">Stores cookie consent preferences</td>
                      <td className="py-3 px-4">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Third-Party Cookies</h2>
              <p className="leading-relaxed">
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and so forth. These third-party services have their own privacy policies and may collect information about your online activities.
              </p>
              <p className="leading-relaxed mt-4">
                Third-party services we may use include:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Google Analytics - for website analytics</li>
                <li>Formspree - for contact form submissions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Managing Cookies</h2>
              <p className="leading-relaxed">
                Most web browsers allow you to control cookies through their settings preferences. However, limiting cookies may impact your experience on our website. Here's how to manage cookies in popular browsers:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>
              <p className="leading-relaxed mt-4">
                You can also opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Do Not Track Signals</h2>
              <p className="leading-relaxed">
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want to have your online activity tracked. Currently, there is no uniform standard for how websites should respond to DNT signals. Our website does not currently respond to DNT signals.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Updates to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-900 rounded-lg">
                <p className="font-semibold text-white">{companyInfo.name}</p>
                <p>{companyInfo.address.street}</p>
                <p>{companyInfo.address.city}, {companyInfo.address.state} {companyInfo.address.zip}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
